import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Building2, MapPin, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import CsvImportDialog from "@/components/admin/CsvImportDialog";

async function searchCommune(query) {
    try {
        const r = await fetch(
            `https://geo.api.gouv.fr/communes?nom=${encodeURIComponent(query)}&boost=population&limit=8&fields=nom,centre,codesPostaux`
        );
        const data = await r.json();
        return data.map((c) => ({
            name: c.nom,
            postcode: (c.codesPostaux || [])[0] || "",
            lat: c.centre?.coordinates?.[1] || 0,
            lon: c.centre?.coordinates?.[0] || 0,
        }));
    } catch { return []; }
}

function SalleDialog({ open, initial, onClose, onSaved }) {
    const isEdit = !!initial?.id;
    const [f, setF] = useState(initial || { name: "", address: "", city: "", postcode: "", capacity: 12, equipments: "", lat: 0, lon: 0 });
    useEffect(() => { setF(initial || { name: "", address: "", city: "", postcode: "", capacity: 12, equipments: "", lat: 0, lon: 0 }); }, [initial, open]);
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState("");
    const [results, setResults] = useState([]);
    const pickCity = (c) => {
        setF({ ...f, city: c.name, postcode: c.postcode, lat: c.lat, lon: c.lon });
        setSearch(""); setResults([]);
    };

    useEffect(() => {
        const t = setTimeout(async () => {
            if (search.length >= 2) setResults(await searchCommune(search));
            else setResults([]);
        }, 280);
        return () => clearTimeout(t);
    }, [search]);

    if (!open) return null;

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (isEdit) await api.patch(`/admin/salles/${initial.id}`, f);
            else await api.post("/admin/salles", f);
            toast.success(isEdit ? "Salle mise à jour" : "Salle créée");
            onSaved(); onClose();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-ink/50 p-4" data-testid="salle-dialog">
            <div className="relative w-full max-w-2xl rounded-3xl bg-white p-8 shadow-premium-lg">
                <button onClick={onClose} className="absolute right-4 top-4 text-brand-smoke hover:text-brand-ink"><X className="h-5 w-5" /></button>
                <h3 className="font-display text-3xl">{isEdit ? "Modifier la salle" : "Nouvelle salle"}</h3>
                <form onSubmit={submit} className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                        <label className="block text-xs font-semibold uppercase tracking-wider">Nom de la salle</label>
                        <input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="salle-field-name" className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3 outline-none focus:border-brand-orange" />
                    </div>
                    <div className="sm:col-span-2">
                        <label className="block text-xs font-semibold uppercase tracking-wider">Adresse</label>
                        <input required value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3 outline-none focus:border-brand-orange" />
                    </div>
                    <div className="sm:col-span-2 relative">
                        <label className="block text-xs font-semibold uppercase tracking-wider">Ville</label>
                        {f.city ? (
                            <div className="mt-2 flex items-center justify-between rounded-xl border border-brand-line bg-[#FAFAFA] px-4 py-3">
                                <span className="flex items-center gap-2 text-sm"><MapPin className="h-4 w-4 text-brand-orange" /><strong>{f.city}</strong> — {f.postcode}</span>
                                <button type="button" onClick={() => setF({ ...f, city: "", postcode: "", lat: 0, lon: 0 })} className="text-xs text-brand-smoke hover:text-red-600">Changer</button>
                            </div>
                        ) : (
                            <>
                                <input placeholder="Rechercher une ville…" value={search} onChange={(e) => setSearch(e.target.value)} className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3 outline-none focus:border-brand-orange" />
                                {results.length > 0 && (
                                    <ul className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-xl border border-brand-line bg-white shadow-premium">
                                        {results.map((c, i) => (
                                            <li key={i}>
                                                <button type="button" onClick={() => pickCity(c)} className="flex w-full items-center justify-between px-4 py-2.5 text-left text-sm hover:bg-[#FAFAFA]">
                                                    <span>{c.name} — {c.postcode}</span>
                                                    <span className="text-xs text-brand-smoke">{c.lat.toFixed(2)}, {c.lon.toFixed(2)}</span>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </>
                        )}
                    </div>
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider">Capacité</label>
                        <input type="number" min={1} max={200} required value={f.capacity} onChange={(e) => setF({ ...f, capacity: parseInt(e.target.value || 12) })} className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3" />
                    </div>
                    <div></div>
                    <div className="sm:col-span-2">
                        <label className="block text-xs font-semibold uppercase tracking-wider">Équipements</label>
                        <textarea rows={2} value={f.equipments} onChange={(e) => setF({ ...f, equipments: e.target.value })} className="mt-2 w-full rounded-xl border border-brand-line px-4 py-3 outline-none focus:border-brand-orange" />
                    </div>
                    <div className="flex justify-end gap-3 sm:col-span-2 pt-2">
                        <button type="button" onClick={onClose} className="rounded-full px-5 py-2.5 text-sm hover:bg-brand-line/50">Annuler</button>
                        <button type="submit" disabled={saving || !f.city} data-testid="salle-dialog-submit" className="rounded-full bg-brand-orange px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#d9772c] disabled:opacity-60">
                            {saving ? "Enregistrement…" : (isEdit ? "Enregistrer" : "Créer")}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default function AdminSalles() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState({ open: false, initial: null });
    const [importOpen, setImportOpen] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/salles");
            setItems(data);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const remove = async (s) => {
        if (!window.confirm(`Supprimer la salle "${s.name}" ?`)) return;
        try { await api.delete(`/admin/salles/${s.id}`); toast.success("Salle supprimée"); load(); }
        catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div className="mx-auto max-w-6xl px-6 py-10" data-testid="page-admin-salles">
            <div className="flex items-end justify-between">
                <div>
                    <h1 className="font-display text-5xl font-normal tracking-tightest">Salles</h1>
                    <p className="mt-2 text-brand-smoke">Lieux où se déroulent vos sessions.</p>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => setImportOpen(true)}
                        data-testid="admin-salles-import-csv"
                        className="inline-flex items-center gap-2 rounded-full border border-brand-line bg-white px-4 py-2.5 text-sm font-medium hover:border-brand-orange hover:text-brand-orange"
                    >
                        <Upload className="h-3.5 w-3.5" /> CSV
                    </button>
                    <button
                        onClick={() => setDialog({ open: true, initial: null })}
                        data-testid="admin-salles-new"
                        className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-5 py-3 text-sm font-semibold text-white hover:bg-[#d9772c]"
                    >
                        <Plus className="h-4 w-4" /> Nouvelle salle
                    </button>
                </div>
            </div>

            <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {loading ? (
                    <div className="col-span-full py-10 text-center text-brand-smoke">Chargement…</div>
                ) : items.length === 0 ? (
                    <div className="col-span-full rounded-2xl border border-dashed border-brand-line bg-white p-10 text-center text-brand-smoke">
                        Aucune salle — créez la première pour pouvoir planifier des sessions.
                    </div>
                ) : (
                    items.map((s) => (
                        <div
                            key={s.id}
                            className={`relative rounded-2xl border bg-white p-6 shadow-premium ${
                                s.is_priority_city ? "border-brand-orange/60" : "border-brand-line"
                            }`}
                            data-testid={`salle-card-${s.id}`}
                            title={s.is_priority_city ? "Salle prioritaire · Cible Cursus Physio. Pensez à compléter l'adresse réelle, le contact et les équipements." : ""}
                        >
                            {s.is_priority_city && (
                                <span
                                    data-testid={`salle-priority-${s.id}`}
                                    className="absolute -top-2.5 left-5 rounded-full bg-brand-orange px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white"
                                >
                                    🎯 Ville prioritaire
                                </span>
                            )}
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-orange/10 text-brand-orange"><Building2 className="h-5 w-5" /></div>
                                    <div>
                                        <div className="font-display text-xl text-brand-ink">{s.name}</div>
                                        <div className="text-xs text-brand-smoke">{s.city} · {s.postcode}</div>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => setDialog({ open: true, initial: s })} data-testid={`salle-edit-${s.id}`} className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-brand-line hover:border-brand-orange"><Pencil className="h-3.5 w-3.5" /></button>
                                    {!s.is_priority_city && (
                                        <button onClick={() => remove(s)} data-testid={`salle-delete-${s.id}`} className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-brand-line hover:border-red-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                                    )}
                                </div>
                            </div>
                            {s.needs_completion && (
                                <div className="mt-3 inline-flex items-center gap-1 rounded-full bg-orange-100 px-2.5 py-1 text-[10px] font-semibold text-orange-700" data-testid={`salle-todo-${s.id}`}>
                                    ⚠️ À compléter
                                </div>
                            )}
                            <div className="mt-4 space-y-1 text-xs text-brand-ink/80">
                                <div>{s.address}</div>
                                <div className="text-brand-smoke">Capacité · {s.capacity} personnes</div>
                                {s.equipments && <div className="mt-2 line-clamp-2 text-brand-smoke">{s.equipments}</div>}
                            </div>
                        </div>
                    ))
                )}
            </div>

            <SalleDialog open={dialog.open} initial={dialog.initial} onClose={() => setDialog({ open: false, initial: null })} onSaved={load} />
            <CsvImportDialog kind="salles" open={importOpen} onClose={() => setImportOpen(false)} onImported={load} />
        </div>
    );
}
